/**
 * Achievements: a flat list of defs + a tiny system that tracks which are
 * unlocked, fires a callback on each NEW unlock (for the toast animation), and
 * auto-unlocks the meta "all achievements" one. The game (main.ts) detects the
 * conditions and calls `unlock(id)`; this module just owns unlocked state.
 */

export interface Achievement {
  id: string;
  name: string;
  desc: string;
}

export const ACH_ALL = "allach"; // meta: every other achievement unlocked

export const ACHIEVEMENTS: Achievement[] = [
  { id: "kills_100", name: "Tin Can Opener", desc: "Destroy 100 total tanks" },
  { id: "kills_1k", name: "Big 1,000", desc: "Destroy 1,000 total tanks" },
  { id: "kills_10k", name: "Bigger 10,000", desc: "Destroy 10,000 total tanks" },
  { id: "kills_100k", name: "Biggest 100,000", desc: "Destroy 100,000 total tanks" },
  { id: "ricochet", name: "Trick Shot", desc: "Destroy a tank with a ricocheting shell" },
  { id: "double", name: "Two Birds", desc: "Destroy two tanks with one shell" },
  { id: "mine_kill", name: "Minesweeper", desc: "Destroy a tank with a mine" },
  { id: "point_blank", name: "In Your Face", desc: "Destroy a tank from point-blank range" },
  { id: "flawless", name: "Untouchable", desc: "Clear a layer without taking a hit" },
  { id: "speedrun", name: "Blitzkrieg", desc: "Clear a layer in under 15 seconds" },
  { id: "pacifist_dodge", name: "Bullet Time", desc: "Survive 20 seconds without firing a shot" },
  { id: "full_house", name: "Full Roster", desc: "Face 8 enemy tanks alive at once" },
  { id: "no_mine", name: "Light Footed", desc: "Clear a layer where enemies laid mines, hitting none" },
  { id: "layer_5", name: "Skill: Ed Zitron", desc: "Reach layer 5" },
  { id: "layer_10", name: "Skill: Justin Bieber", desc: "Reach layer 10" },
  { id: "layer_15", name: "Skill: Rebecca Black", desc: "Reach layer 15" },
  { id: "layer_20", name: "Skill: Noob", desc: "Reach layer 20" },
  { id: "layer_25", name: "Skill: Average", desc: "Reach layer 25" },
  { id: "layer_30", name: "Skill: Pro", desc: "Reach layer 30" },
  { id: "layer_40", name: "Skill: H4xxor", desc: "Reach layer 40" },
  { id: "layer_50", name: "Skill: Legend", desc: "Reach layer 50" },
  { id: "layer_100", name: "Skill: Chuck Norris", desc: "Reach layer 100" },
  { id: ACH_ALL, name: "Your Grass Bores Me", desc: "Unlock every achievement" },
];

/** Achievement id → wearable-flair slug (lowercase + hyphens; underscores out). */
export function flairSlug(id: string): string {
  return id.replace(/_/g, "-");
}

/**
 * Every achievement is wearable as a cosmetic flair. `value` is the slug; `ach`
 * is the achievement required (the picker shows only earned ones); `name` is the
 * chip label.
 */
export const FLAIR_LABELS: Array<{ value: string; ach: string; name: string }> = ACHIEVEMENTS.map((a) => ({
  value: flairSlug(a.id),
  ach: a.id,
  name: a.name,
}));

export class AchievementSystem {
  private unlocked = new Set<string>();
  /** called once per NEW unlock (animation + persistence) */
  onUnlock: ((a: Achievement) => void) | null = null;
  /** true while the current match is dev-mode tainted — unlocks are ignored */
  suspended = false;

  setUnlocked(ids: string[]) {
    this.unlocked = new Set(ids.filter((id) => ACHIEVEMENTS.some((a) => a.id === id)));
  }

  has(id: string): boolean {
    return this.unlocked.has(id);
  }

  unlockedIds(): string[] {
    return [...this.unlocked];
  }

  get total(): number {
    return ACHIEVEMENTS.length;
  }

  get count(): number {
    return this.unlocked.size;
  }

  /**
   * Remove an unlocked achievement (dev tool). Idempotent. Removing any normal
   * achievement also clears the "all achievements" meta, since the set is no
   * longer complete. Does NOT fire onUnlock; the caller handles persistence.
   */
  lock(id: string) {
    if (!this.unlocked.has(id)) return;
    this.unlocked.delete(id);
    if (id !== ACH_ALL) this.unlocked.delete(ACH_ALL);
  }

  /** Unlock by id (idempotent). Fires onUnlock for genuinely new unlocks. */
  unlock(id: string) {
    if (this.suspended) return;
    if (this.unlocked.has(id)) return;
    const a = ACHIEVEMENTS.find((x) => x.id === id);
    if (!a) return;
    this.unlocked.add(id);
    this.onUnlock?.(a);
    // meta: did that complete the whole set?
    if (id !== ACH_ALL && ACHIEVEMENTS.every((x) => x.id === ACH_ALL || this.unlocked.has(x.id))) {
      this.unlock(ACH_ALL);
    }
  }
}
